<div class="container">
  <h4 class="mb-3">Excel lesson import</h4>
  <p class="text-muted small mb-4">
    Create a lesson filled from <code>ContentImportFormat.xlsx</code> (no AI).
    Flow: create lesson → upload Excel → check → upload image ZIP (optional) → populate TOC &amp; slides.
    Use Excel <strong>SlideID</strong> values as-is. Options:
    <code>3|4*|5</code> (* = correct) or JSON options array.
    ZIP layout is <code>&lt;slideId&gt;/&lt;imageFileName&gt;</code>
    (e.g. <code>SL_intro_01/diagram.png</code>) for upload and download. Slide folders are created if missing; existing files prompt for overwrite.
  </p>

  <div class="card mb-3">
    <div class="card-header">1. Lesson</div>
    <div class="card-body">
      <div class="row g-2 align-items-end mb-3">
        <div class="col-md-7">
          <label class="form-label">Excel-import lesson</label>
          <select class="form-select" [(ngModel)]="selectedLessonId" [disabled]="uploading || populating">
            <option [ngValue]="null">— Select —</option>
            <option *ngFor="let l of lessons" [ngValue]="l.lessonId">
              {{ l.lessonId }} — {{ l.lessonTitle }} ({{ l.lessonStatus }}{{ l.hasExcel ? ', Excel' : '' }})
            </option>
          </select>
        </div>
        <div class="col-md-3">
          <button type="button" class="btn btn-outline-secondary" (click)="reload()" [disabled]="uploading || populating">
            Refresh
          </button>
        </div>
      </div>

      <div class="border rounded p-3 bg-light">
        <div class="fw-semibold mb-2">Or create new Excel-import lesson</div>
        <div class="row g-2 align-items-end">
          <div class="col-md-5">
            <label class="form-label">Title</label>
            <input class="form-control" [(ngModel)]="newTitle" maxlength="200" />
          </div>
          <div class="col-md-4">
            <label class="form-label">Subject (optional)</label>
            <input class="form-control" [(ngModel)]="newSubject" maxlength="200" />
          </div>
          <div class="col-md-3">
            <button type="button" class="btn btn-primary w-100" (click)="createLesson()" [disabled]="creating">
              {{ creating ? 'Creating…' : 'Create' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="card mb-3">
    <div class="card-header">2. Excel &amp; populate</div>
    <div class="card-body">
      <div class="d-flex flex-wrap gap-2 align-items-center mb-3">
        <label class="btn btn-outline-primary mb-0" [class.disabled]="selectedLessonId == null || uploading || populating">
          {{ uploading ? 'Uploading…' : 'Upload Excel' }}
          <input
            type="file"
            accept=".xlsx,.xls"
            hidden
            (change)="onExcelSelected($event)"
            [disabled]="selectedLessonId == null || uploading || populating" />
        </label>
        <span class="text-muted small" *ngIf="selectedFileName">{{ selectedFileName }}</span>

        <button
          type="button"
          class="btn btn-outline-secondary"
          (click)="checkExcel()"
          [disabled]="selectedLessonId == null || checking || uploading || populating">
          {{ checking ? 'Checking…' : 'Check Excel' }}
        </button>

        <label
          class="btn btn-outline-secondary mb-0"
          [class.disabled]="selectedLessonId == null || uploadingZip || downloadingZip || uploading || populating">
          {{ uploadingZip ? 'Extracting ZIP…' : 'Upload image ZIP' }}
          <input
            type="file"
            accept=".zip,application/zip"
            hidden
            (change)="onZipSelected($event)"
            [disabled]="selectedLessonId == null || uploadingZip || downloadingZip || uploading || populating" />
        </label>
        <button
          type="button"
          class="btn btn-outline-secondary"
          (click)="downloadImageZip()"
          [disabled]="selectedLessonId == null || uploadingZip || downloadingZip || uploading || populating">
          {{ downloadingZip ? 'Preparing ZIP…' : 'Download image ZIP' }}
        </button>
        <span class="text-muted small" *ngIf="selectedZipName">{{ selectedZipName }}</span>

        <button
          type="button"
          class="btn btn-success"
          (click)="populate()"
          [disabled]="selectedLessonId == null || populating || uploading || uploadingZip || downloadingZip">
          {{ populating ? 'Populating…' : 'Populate lesson' }}
        </button>
      </div>

      <div *ngIf="zipConflicts?.length" class="alert alert-warning py-2 small">
        ZIP conflicts (confirm overwrite when prompted): {{ zipConflicts.length }} file(s)
      </div>

      <div *ngIf="lastMessage" class="small text-muted mb-2">{{ lastMessage }}</div>

      <div *ngIf="errors?.length" class="alert alert-danger py-2">
        <div class="fw-semibold mb-1">Errors (import blocked)</div>
        <ul class="mb-0 ps-3">
          <li *ngFor="let e of errors">{{ e }}</li>
        </ul>
      </div>

      <div *ngIf="warnings?.length" class="alert alert-warning py-2">
        <div class="fw-semibold mb-1">Warnings</div>
        <ul class="mb-0 ps-3">
          <li *ngFor="let w of warnings">{{ w }}</li>
        </ul>
      </div>

      <div *ngIf="preview" class="border rounded p-3">
        <div class="fw-semibold mb-2">
          Preview
          <span *ngIf="checkOk === true" class="badge text-bg-success ms-2">OK</span>
          <span *ngIf="checkOk === false" class="badge text-bg-danger ms-2">Invalid</span>
        </div>
        <div class="small text-muted mb-2">
          Sections: {{ preview.sectionCount }} · Slides: {{ preview.contentSlideCount }} · Questions:
          {{ preview.questionCount }}
        </div>
        <div *ngFor="let s of preview.sections || []" class="mb-2">
          <div class="fw-semibold">{{ s.title }}</div>
          <ul class="mb-0 small">
            <li *ngFor="let sl of s.slides || []">
              {{ sl.slideId }} — {{ sl.title }} ({{ sl.slideType }}, {{ sl.elementCount }} elements)
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</div>
